import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  Field,
  Icon,
  Input,
  PageHead,
  Pager,
  Select,
  StateBlock,
  useToast,
  type ActiveFilter,
  type Column,
} from '@w7/ui'
import {
  useMyPnlNew,
  useMyPnlNewEvents,
  useMyPnlNewMarkets,
  useMyPnlNewUsers,
} from '@/api/hooks/reports/useMyPnlNew'
import {
  MY_PNL_EVENT_NAME_PREFIX,
  MY_PNL_PAGE_SIZE,
  filterMyPnlRowsForEvent,
  injectMyPnlDrillRows,
  mergeMyPnlEventOptions,
  myPnlSortTarget,
  type MyPnlEventRow,
} from '@/api/map/myPnlNew'
import { Money } from '@/app/bits'
import { EMPTY, count, hms, isoDay, ll } from '@/lib/format'
import {
  LAB_MY_PNL_SUB_GAMES,
  labMyPnlEventTypes,
  labMyPnlFiltersNarrowed,
  labMyPnlHidesDrilldown,
  labMyPnlHidesEventFilters,
  labMyPnlNewRangeTooLong,
  labMyPnlTone,
} from './myPnlNew'
import './my-pnl.css'

export type { MyPnlEventRow }

type Applied = {
  from: string
  to: string
  subGame: string
  eventId: string
  typeName: string
  run: number
}

function LabAmt({ value }: { value: number }) {
  return <Money value={value} tone={labMyPnlTone(value)} currency="INR" symbol={false} />
}

/**
 * My P/L New — Lab Aggregate `/aggregate/master-dashboard-new` + Aggregate-API
 * getPLBySubGameForAggregatorBq / getMarketWithinEventAggregatorBq /
 * getUsersWithinEventAggregatorBq / getEventNameAndIdBq (7-day max).
 */
export function MyPnlNewPage() {
  const toast = useToast()
  const today = isoDay(new Date())
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [subGame, setSubGame] = useState('All')
  const [eventId, setEventId] = useState('All')
  const [typeName, setTypeName] = useState('All')
  const [page, setPage] = useState(1)
  const [applied, setApplied] = useState<Applied>({
    from: today,
    to: today,
    subGame: 'All',
    eventId: 'All',
    typeName: 'All',
    run: 1,
  })
  const [expand, setExpand] = useState<{ rowId: string; kind: 'market' | 'user' | null }>({
    rowId: '',
    kind: null,
  })

  const hideEventFilters = labMyPnlHidesEventFilters(subGame)
  const hideAppliedEvents = labMyPnlHidesEventFilters(applied.subGame)
  const typeOptions = useMemo(
    () => labMyPnlEventTypes(subGame).map((t) => ({ value: t, label: t })),
    [subGame],
  )
  const subGameOptions = useMemo(
    () => LAB_MY_PNL_SUB_GAMES.map((name) => ({ value: name, label: name })),
    [],
  )

  const filter = {
    from: applied.from,
    to: applied.to,
    subGame: applied.subGame,
    eventId: hideAppliedEvents ? 'All' : applied.eventId,
    typeName: hideAppliedEvents ? 'All' : applied.typeName,
    page,
    run: applied.run,
  }

  const listQ = useMyPnlNew(filter)
  const eventsQ = useMyPnlNewEvents(
    {
      from: fromDate,
      to: toDate,
      subGame,
      run: applied.run,
    },
    !hideEventFilters,
  )

  const rawRows = listQ.data?.rows ?? []
  const rows = useMemo(
    () =>
      hideAppliedEvents
        ? rawRows
        : filterMyPnlRowsForEvent(rawRows, applied.eventId || 'All'),
    [rawRows, applied.eventId, hideAppliedEvents],
  )
  const total = listQ.data?.total ?? 0
  const loading = listQ.isFetching
  const pageSize = listQ.data?.maxRow || MY_PNL_PAGE_SIZE
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1)
  const narrowed = labMyPnlFiltersNarrowed(applied)
  /** Lab old uses list `totalSum` (no separate grand-total endpoint). */
  const pageSum = listQ.data?.pageSum
  const grand = {
    win: pageSum?.win ?? 0,
    commission: pageSum?.commission ?? 0,
    netAmt: pageSum?.netAmt ?? 0,
    winInr: pageSum?.winINR ?? 0,
    commissionInr: pageSum?.commissionINR ?? 0,
    netAmtInr: pageSum?.netAmtINR ?? 0,
    betFairAmount: pageSum?.betFairAmount ?? 0,
  }

  const eventOptions = useMemo(() => {
    if (hideEventFilters) return [{ value: 'All', label: 'All' }]
    return mergeMyPnlEventOptions(eventsQ.data ?? [{ value: 'All', label: 'All' }], rawRows)
  }, [eventsQ.data, hideEventFilters, rawRows])

  const expandedRow = rows.find((r) => r.id === expand.rowId) ?? null
  const marketsQ = useMyPnlNewMarkets(filter, expandedRow, expand.kind === 'market')
  const usersQ = useMyPnlNewUsers(filter, expandedRow, expand.kind === 'user')

  useEffect(() => {
    if (!expand.rowId) return
    if (rows.some((r) => r.id === expand.rowId)) return
    setExpand({ rowId: '', kind: null })
  }, [rows, expand.rowId])

  const bumpApplied = (next: Omit<Applied, 'run'>) => {
    setPage(1)
    setExpand({ rowId: '', kind: null })
    setApplied((prev) => ({ ...next, run: prev.run + 1 }))
  }

  const onSearch = () => {
    if (labMyPnlNewRangeTooLong(fromDate, toDate)) {
      toast.error('Date difference should not be greater than 7 days')
      return
    }
    bumpApplied({
      from: fromDate,
      to: toDate,
      subGame,
      eventId: hideEventFilters ? 'All' : eventId,
      typeName: hideEventFilters ? 'All' : typeName,
    })
  }

  const resetFilters = () => {
    const day = isoDay(new Date())
    setFromDate(day)
    setToDate(day)
    setSubGame('All')
    setEventId('All')
    setTypeName('All')
    bumpApplied({
      from: day,
      to: day,
      subGame: 'All',
      eventId: 'All',
      typeName: 'All',
    })
  }

  const activeFilters = useMemo((): ActiveFilter[] => {
    const chips: ActiveFilter[] = []
    if (applied.subGame && applied.subGame !== 'All') {
      chips.push({
        id: 'subGame',
        label: `Sub Game: ${applied.subGame}`,
        removeLabel: 'Clear Sub Game filter',
        onRemove: () => {
          setSubGame('All')
          setEventId('All')
          setTypeName('All')
          bumpApplied({
            from: applied.from,
            to: applied.to,
            subGame: 'All',
            eventId: 'All',
            typeName: 'All',
          })
        },
      })
    }
    if (!hideAppliedEvents && applied.eventId && applied.eventId !== 'All') {
      const eventLabel =
        eventOptions.find((o) => o.value === applied.eventId)?.label ||
        (applied.eventId.startsWith(MY_PNL_EVENT_NAME_PREFIX)
          ? applied.eventId.slice(MY_PNL_EVENT_NAME_PREFIX.length)
          : applied.eventId)
      chips.push({
        id: 'event',
        label: `Event: ${eventLabel}`,
        removeLabel: 'Clear event filter',
        onRemove: () => {
          setEventId('All')
          bumpApplied({
            from: applied.from,
            to: applied.to,
            subGame: applied.subGame,
            eventId: 'All',
            typeName: applied.typeName,
          })
        },
      })
    }
    if (!hideAppliedEvents && applied.typeName && applied.typeName !== 'All') {
      chips.push({
        id: 'type',
        label: `Type: ${applied.typeName}`,
        removeLabel: 'Clear event type filter',
        onRemove: () => {
          setTypeName('All')
          bumpApplied({
            from: applied.from,
            to: applied.to,
            subGame: applied.subGame,
            eventId: applied.eventId,
            typeName: 'All',
          })
        },
      })
    }
    return chips
  }, [applied, eventOptions, hideAppliedEvents])

  const onDrill = (row: MyPnlEventRow, kind: 'market' | 'user') => {
    if (expand.rowId === row.id && expand.kind === kind) {
      setExpand({ rowId: '', kind: null })
      return
    }
    setExpand({ rowId: row.id, kind })
  }

  const drillRows =
    expand.kind === 'market' ? (marketsQ.data ?? []) : expand.kind === 'user' ? (usersQ.data ?? []) : []
  const drillLoading =
    expand.kind === 'market' ? marketsQ.isFetching : expand.kind === 'user' ? usersQ.isFetching : false

  /**
   * Lab nests market/user rows under the event. Inject after the parent; drill
   * sortValues mirror the parent so DataTable’s active sort (e.g. Bet Count)
   * stays put (stable sort keeps drills immediately under that event).
   */
  const tableRows = useMemo(() => {
    if (!expand.kind || !expand.rowId || !expandedRow) return rows
    const nested = injectMyPnlDrillRows(rows, expand.rowId, drillRows)
    return hideAppliedEvents
      ? nested
      : filterMyPnlRowsForEvent(nested, applied.eventId || 'All')
  }, [
    rows,
    expand.kind,
    expand.rowId,
    expandedRow,
    drillRows,
    applied.eventId,
    hideAppliedEvents,
  ])

  const tableKey = [
    applied.run,
    applied.from,
    applied.to,
    applied.subGame,
    applied.eventId,
    applied.typeName,
    page,
  ].join('|')

  const eventById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])

  const columns = useMemo<Column<MyPnlEventRow>[]>(() => {
    const sortOf = <T extends string | number>(pick: (r: MyPnlEventRow) => T) => ({
      sortValue: (row: MyPnlEventRow) => pick(myPnlSortTarget(row, eventById)),
    })
    return [
      {
        id: 'date',
        header: 'Date',
        width: 120,
        cell: (row) =>
          row.drill ? null : (
            <span className="w7-bm-placed">
              <span className="w7-bm-placed-day">{ll(row.settledAt)}</span>
              <span className="w7-bm-placed-time">{hms(row.settledAt)}</span>
            </span>
          ),
        ...sortOf((r) => r.settledAt),
        total: 'Grand Total',
      },
      {
        id: 'game',
        header: 'Game',
        cell: (row) => (row.drill ? null : row.gameName || EMPTY),
        ...sortOf((r) => r.gameName),
        total: '—',
      },
      {
        id: 'subGame',
        header: 'SubGame',
        cell: (row) => (row.drill ? null : row.subGame || EMPTY),
        ...sortOf((r) => r.subGame),
        total: '—',
      },
      {
        id: 'event',
        header: 'Event',
        cell: (row) => (
          <span className={`w7-bm-event-cell${row.drill ? ' cm-my-pnl-drill-label' : ''}`}>
            <span className="w7-bm-event-name">{row.eventName || EMPTY}</span>
          </span>
        ),
        ...sortOf((r) => r.eventName),
        total: '—',
      },
      {
        id: 'winInr',
        header: 'Win (INR)',
        numeric: true,
        cell: (row) => <LabAmt value={row.winInr} />,
        ...sortOf((r) => r.winInr),
        total: <LabAmt value={grand.winInr} />,
      },
      {
        id: 'netInr',
        header: 'NetAmount (INR)',
        numeric: true,
        cell: (row) => <LabAmt value={row.netAmtInr} />,
        ...sortOf((r) => r.netAmtInr),
        total: <LabAmt value={grand.netAmtInr} />,
      },
      {
        id: 'users',
        header: 'User Count',
        numeric: true,
        cell: (row) => count(row.userCount),
        ...sortOf((r) => r.userCount),
        total: '—',
      },
      {
        id: 'bets',
        header: 'Bet Count',
        numeric: true,
        cell: (row) => count(row.betCount),
        ...sortOf((r) => r.betCount),
        total: '—',
      },
      {
        id: 'actions',
        header: '',
        width: 160,
        cell: (row) =>
          row.drill || labMyPnlHidesDrilldown(row.gameName) ? null : (
            <span className="cm-my-pnl-actions">
              <Button
                type="button"
                size="sm"
                variant="danger"
                className="cm-my-pnl-btn-market"
                busy={expand.rowId === row.id && expand.kind === 'market' && marketsQ.isFetching}
                aria-pressed={expand.rowId === row.id && expand.kind === 'market'}
                onClick={(e) => {
                  e.stopPropagation()
                  onDrill(row, 'market')
                }}
              >
                Market
              </Button>
              <Button
                type="button"
                size="sm"
                variant="primary"
                className="cm-my-pnl-btn-user"
                busy={expand.rowId === row.id && expand.kind === 'user' && usersQ.isFetching}
                aria-pressed={expand.rowId === row.id && expand.kind === 'user'}
                onClick={(e) => {
                  e.stopPropagation()
                  onDrill(row, 'user')
                }}
              >
                User
              </Button>
            </span>
          ),
        total: '',
      },
    ]
  }, [eventById, expand.kind, expand.rowId, grand, marketsQ.isFetching, usersQ.isFetching])

  return (
    <>
      <PageHead
        title="My P/L New"
        subtitle={`Settled book P/L · ${applied.from} → ${applied.to}`}
      />
      <div className="cm-my-pnl">
      <div className="cm-my-pnl-filters" role="region" aria-label="My P/L New filters">
        <Field label="From Date" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              required
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value)
                if (!hideEventFilters) setEventId('All')
              }}
            />
          )}
        </Field>
        <Field label="To Date" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              required
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value)
                if (!hideEventFilters) setEventId('All')
              }}
            />
          )}
        </Field>
        <Field label="Sub Games">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={subGame}
              onChange={(e) => {
                setSubGame(e.target.value)
                setEventId('All')
                setTypeName('All')
              }}
              options={subGameOptions}
            />
          )}
        </Field>
        {!hideEventFilters ? (
          <>
            <Field label="Select Event Name">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  options={eventOptions}
                />
              )}
            </Field>
            <Field label="Select Event Type">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                  options={typeOptions}
                />
              )}
            </Field>
          </>
        ) : null}
        <div className="cm-my-pnl-filters-actions">
          <Button type="button" variant="primary" onClick={onSearch} busy={loading}>
            Search
          </Button>
        </div>
        {activeFilters.length > 0 ? (
          <div className="cm-my-pnl-filter-chips" data-testid="my-pnl-filter-chips">
            {activeFilters.map((filter) => (
              <span key={filter.id} className="w7-activechip">
                <span className="w7-truncate">{filter.label}</span>
                <button
                  type="button"
                  aria-label={filter.removeLabel}
                  title={filter.removeLabel}
                  onClick={filter.onRemove}
                >
                  <Icon name="close" size={12} />
                </button>
              </span>
            ))}
            {narrowed ? (
              <Button variant="ghost" size="sm" icon="close" onClick={resetFilters}>
                Clear all
              </Button>
            ) : null}
          </div>
        ) : null}
        <span className="w7-sr" aria-live="polite">
          {narrowed
            ? `Showing ${total} rows with ${activeFilters.length} filters applied`
            : `Showing ${total} rows`}
        </span>
      </div>

      {loading && rows.length === 0 ? (
        <div className="w7-bm-results">
          <div className="w7-bm-table-wrap">
            <DataTable
              label="My P/L New"
              rows={[]}
              columns={columns}
              rowKey={(row) => row.id}
              density="compact"
              stickyOffset={0}
              loading
              empty={null}
            />
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="w7-bm-empty-panel" data-testid="my-pnl-new-empty">
          <StateBlock
            icon={narrowed ? 'filter' : 'pnl'}
            title={
              narrowed ? 'No results match these filters' : 'Nothing settled in this range'
            }
            actions={
              narrowed ? (
                <Button variant="primary" icon="close" onClick={resetFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          >
            {narrowed
              ? 'Widen the dates, clear a Sub Game or event, or reset filters to see My P/L New again.'
              : `No My P/L New for Sub Game “${applied.subGame}” from ${applied.from} to ${applied.to}. Widen the dates or pick another Sub Game.`}
          </StateBlock>
        </div>
      ) : (
        <div className="w7-bm-results">
          <div className="w7-bm-table-wrap">
            <DataTable
              key={tableKey}
              label="My P/L New by settled event"
              rows={tableRows}
              columns={columns}
              rowKey={(row) => row.id}
              density="compact"
              stickyOffset={0}
              showTotals
              totalsLabel="Grand Total"
              initialSort={{ columnId: 'date', direction: 'desc' }}
              empty={null}
            />
          </div>
          {expand.kind && !drillLoading && drillRows.length === 0 ? (
            <p className="cm-my-pnl-drill-empty" role="status">
              No {expand.kind === 'market' ? 'markets' : 'users'} for this event
              {expandedRow?.eventName ? ` · ${expandedRow.eventName}` : ''}.
            </p>
          ) : null}
          <div className="w7-bm-pager">
            <Pager
              page={Math.min(page, pageCount)}
              pageCount={pageCount}
              total={total}
              pageSize={pageSize}
              onChange={(p) => {
                setPage(p)
                setExpand({ rowId: '', kind: null })
              }}
            />
          </div>
        </div>
      )}
      </div>
    </>
  )
}
